import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import {
  placeAnchored,
  rootZoom,
  useDismissable,
  type PeekAnchor,
  type Placement,
} from '../lib/popover'

// ============================================================================
// THE ANCHORED PANEL — one component, three callers so far (the task row's edit menu, the chat
// thread row's delete menu, and whatever the next right-click is), and it exists because the
// third one was about to copy the second.
//
// IT OWNS ALMOST NOTHING, WHICH IS THE POINT. The geometry is `lib/popover.ts`'s — `placeAnchored`
// picks the side with room and clamps into the viewport, `rootZoom` measures the CSS `zoom` the
// app runs at, and `useDismissable` is Escape + outside-pointerdown + the swallowed click. All
// three are unit-tested where they live (lib/popover.test.ts). Reimplementing any of them here is
// how the owner's "pops up in random places, sometimes off the screen completely" bug comes back:
// under `zoom: 1.3` a `getBoundingClientRect()` number assigned straight to a `position: fixed`
// panel's `left` gets multiplied by 1.3 again on paint. popover.ts's header tells that story in
// full; this file's job is to never retell it in code.
//
// SO WHAT IS ACTUALLY HERE, and could not be there:
//
//   1. A RE-PLACE WHEN THE PANEL'S OWN HEIGHT CHANGES. `useAnchoredPlacement` re-measures only
//      when the ANCHOR moves, which is right for the calendar peek (fixed contents, placed once)
//      and wrong for every menu: the thread menu grows a delete confirmation under the click, and
//      the task menu's list picker is as tall as the board has lists. So the two pure functions
//      underneath that hook are used directly, in a layout effect keyed on the anchor AND on
//      `children` — which is a superset of naming each caller's own height inputs and, unlike
//      naming them, does not require this file to know what its callers put inside it. It costs one extra measurement per parent render, and a
//      measurement is idempotent.
//   2. ONLY ONE IS OPEN, ACROSS COMPONENTS. A module-level registry rather than a context,
//      because a task menu and a thread menu are different trees and there is exactly one pointer.
//      `useDismissable` closes a panel when you press somewhere else; this closes it when you
//      right-click straight onto another row, where there is no "somewhere else" in between.
//
// WHAT IT STILL DOES NOT OWN: what is inside it. Every caller brings its own controls and its own
// copy. This is a positioner and a dismisser — not a menu vocabulary, and not a `role="menu"`:
// both panels that use it hold form controls (a date field, a select), which a menu role would lie
// about to a screen reader. `role="dialog"` WITHOUT `aria-modal`, the shape components/calendar/
// Peek.tsx settled on, because it traps no focus.
//
// SUPPRESSING THE NATIVE MENU IS THE CALLER'S JOB, ON THE ELEMENT THAT HAS A REPLACEMENT. There is
// no document-level `preventDefault` on `contextmenu` anywhere in this codebase and there must not
// be: right-click on a paragraph, a link or a text field has to keep giving the browser's own menu
// — copy, open in new tab, spellcheck — or the app has taken something away and put nothing back.
// ============================================================================

/**
 * The rectangle the panel is drawn beside — viewport pixels, captured once at open time.
 *
 * A CURSOR IS A ZERO-SIZED RECTANGLE, which is why a right-click and a button press can travel
 * through the same field: `anchorAtPoint(x, y)` from lib/popover.ts collapses the pointer into
 * one of these, `anchorOfElement(el)` narrows a trigger's box into the same four numbers.
 */
export type MenuAnchor = PeekAnchor

/**
 * The one open panel, or null. Module-level on purpose — see the header. It holds a `close` the
 * owner supplied, so closing is whatever that owner's own state change is.
 */
let openPanel: { close: () => void } | null = null

export function ContextMenu({
  anchor,
  label,
  width,
  onClose,
  children,
}: {
  anchor: MenuAnchor
  /** What this panel is, for a screen reader. Name the thing it acts on, not the control. */
  label: string
  /** The design width. Also the fallback the first paint reasons about, before there is a box. */
  width: number
  onClose: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [placement, setPlacement] = useState<Placement>({ left: anchor.left, top: anchor.bottom })

  useLayoutEffect(() => {
    // MEASURE, THEN PLACE, IN VIEWPORT PIXELS, DIVIDED BY THE ZOOM EXACTLY ONCE. This effect only
    // supplies the three numbers lib/popover.ts's pure functions need — the panel's real box, the
    // viewport, the anchor — and does the one division on the way out, the same shape
    // `useAnchoredPlacement` has internally.
    const zoom = rootZoom(typeof document === 'undefined' ? null : document)
    const box = ref.current?.getBoundingClientRect()
    // A first paint with no box yet still needs a width to reason about; the design constant,
    // scaled, is the right guess because it is what the CSS asked for.
    const panel = { width: box?.width || width * zoom, height: box?.height ?? 0 }
    const viewport = { width: window.innerWidth, height: window.innerHeight }
    const placed = placeAnchored(anchor, panel, viewport)
    setPlacement({ left: placed.left / zoom, top: placed.top / zoom })
    // `children` is a dependency on purpose, not eslint noise: a menu that grows a delete
    // confirmation is a taller panel that has to be re-placed, or it hangs off the bottom of the
    // screen. React compares it by identity, so this re-measures whenever the owner re-rendered —
    // which is exactly when the contents could have changed, and a superset of naming each
    // caller's own height inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor.top, anchor.left, anchor.right, anchor.bottom, width, children])

  // Escape, outside pointerdown, and the click that closed is eaten rather than acted on.
  useDismissable(ref, onClose)

  // ONE AT A TIME. Registered here rather than at each open call site so every caller gets the
  // rule for free, including the one that has not been written yet.
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const self = { close: () => close.current() }
    const previous = openPanel
    openPanel = self
    if (previous) previous.close()
    return () => {
      if (openPanel === self) openPanel = null
    }
  }, [])

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      className="fixed z-30 flex flex-col gap-[9px] rounded-panel border border-line-2 bg-surface p-[13px] shadow-lifted"
      style={{ width, left: placement.left, top: placement.top }}
    >
      {children}
    </div>
  )
}
